<template>
  <dependants-coverage-table :dependants="groupedDependants" :loading="loading">
    <template
      v-if="
        showActions &&
        user_role != 'rrhh' &&
        !ticketLoading &&
        ![TICKET.STATES.CANCELED, TICKET.STATES.EXECUTED].includes(
          ticket.state?.id
        )
      "
      #action="{ record }"
    >
      <div style="display: flex; align-items: center">
        <a-tooltip placement="topLeft" title="Asignar coberturas">
          <a-button
            type="link"
            :disabled="!isTicketEditable"
            @click="() => openCoverageModal(record)"
          >
            <setting-outlined class="blue-icon" />
          </a-button>
        </a-tooltip>
        <a-tooltip placement="topLeft" title="Eliminar">
          <a-popconfirm
            title="¿Estás seguro de eliminar esta carga?"
            ok-text="Sí"
            cancel-text="No"
            @confirm="() => deleteDependant(record.id, record.ticketId)"
          >
            <a-button type="link">
              <delete-outlined class="red-icon" />
            </a-button>
          </a-popconfirm>
        </a-tooltip>
      </div>
    </template>
  </dependants-coverage-table>

  <a-modal
    v-model:visible="coverageModal.visible"
    :title="coverageModal.title"
    :confirm-loading="coverageModal.saving"
    :ok-button-props="{ disabled: !isTicketEditable }"
    ok-text="Guardar"
    cancel-text="Cancelar"
    destroyOnClose
    @ok="saveDependentCoverages"
    @cancel="closeCoverageModal"
  >
    <a-alert
      v-if="!isTicketEditable"
      type="warning"
      show-icon
      message="Este ticket no está editable."
      description="Solo se puede editar la selección de coberturas en estado Creado, En revisión corredora, o En revisión aseguradora."
      class="mb-3"
    />
    <a-spin :spinning="coverageModal.loading">
      <a-form layout="vertical">
        <a-form-item
          label="Coberturas seleccionadas"
          required
          :help="
            coverageModal.selectedEmployeeCoverageIds.length === 0
              ? 'Debes seleccionar al menos una cobertura.'
              : ''
          "
          :validate-status="
            coverageModal.selectedEmployeeCoverageIds.length === 0
              ? 'error'
              : 'success'
          "
        >
          <a-select
            v-model:value="coverageModal.selectedEmployeeCoverageIds"
            mode="multiple"
            :options="availableCoverageOptions"
            :disabled="!isTicketEditable || coverageModal.loading"
            :max-tag-count="2"
            placeholder="Selecciona coberturas"
          />
        </a-form-item>
      </a-form>
    </a-spin>
  </a-modal>
</template>

<script setup>
import { computed, reactive, defineProps } from 'vue';
import { useStore } from 'vuex';
import {
  FETCH_TICKET_AVAILABLE_COVERAGES,
  FETCH_TICKET_DEPENDANTS_INCORPORATIONS,
  FETCH_TICKET_DEPENDENT_INCORPORATION_COVERAGES,
  GET_TICKET_AVAILABLE_COVERAGES,
  GET_TICKET_DEPENDANTS_INCORPORATIONS,
  GET_TICKET,
  PUT_TICKET_DEPENDENT_INCORPORATION_COVERAGES,
  DELETE_TICKET_DEPENDANT_INCORPORATION,
  GET_LOGGED_USER
} from '@/store/types';
import TICKET from '@/constants/ticket';
import { DeleteOutlined, SettingOutlined } from '@ant-design/icons-vue';
import notification from '@/utils/notifications';
import DependantsCoverageTable from '@/new-components/ticket/dependants-coverage-table.vue';
import { groupTicketDependants } from '@/utils/incorporationDetailGroups';

const props = defineProps({
  dependants: {
    type: Array,
    default: null
  },
  loading: {
    type: Boolean,
    default: null
  },
  showActions: {
    type: Boolean,
    default: true
  }
});

const store = useStore();

const ticket = computed(() => store.getters[GET_TICKET]?.data);
const ticketLoading = computed(() => store.getters[GET_TICKET]?.loading);
const dependants = computed(
  () =>
    props.dependants ??
    store.getters[GET_TICKET_DEPENDANTS_INCORPORATIONS]?.data ??
    []
);
const loading = computed(
  () =>
    props.loading ??
    store.getters[GET_TICKET_DEPENDANTS_INCORPORATIONS]?.loading ??
    false
);
const user_role = computed(
  () => store.getters[GET_LOGGED_USER]?.roleName || 'rrhh'
);
const availableCoverages = computed(
  () => store.getters[GET_TICKET_AVAILABLE_COVERAGES] || []
);
const availableCoverageOptions = computed(() =>
  availableCoverages.value.map((coverage) => ({
    label: `${coverage?.policy?.numberPolicy || '-'} | ${
      coverage?.groupCompany?.name || '-'
    } | ${coverage?.coverage?.name || '-'}${
      coverage?.coverage?.required ? ' (Requerida)' : ''
    }`,
    value:
      coverage?.employeeCoverageId || coverage?.operationalId || coverage?.id,
    disabled: Boolean(coverage?.coverage?.required)
  }))
);
const isTicketEditable = computed(
  () =>
    ticket.value?.state?.id === 1 || // CREATED
    ticket.value?.state?.id === 7 || // UNDER_REVIEW_BEWELL
    ticket.value?.state?.id === 8 // UNDER_REVIEW_INSURANCE_COMPANY
);

const coverageModal = reactive({
  visible: false,
  loading: false,
  saving: false,
  title: 'Asignar coberturas',
  dependentIncorporationId: null,
  selectedEmployeeCoverageIds: []
});

const getApiMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const groupedDependants = computed(() =>
  groupTicketDependants(dependants.value, availableCoverages.value)
);

const deleteDependant = async (id, ticketId) => {
  try {
    await store.dispatch(DELETE_TICKET_DEPENDANT_INCORPORATION, {
      id,
      ticketId
    });
  } catch (error) {
    console.error('Error deleting dependant', error);
    notification(
      'error',
      'Error',
      'No se pudo eliminar la carga. Intente de nuevo.'
    );
  }
};

const closeCoverageModal = () => {
  coverageModal.visible = false;
  coverageModal.loading = false;
  coverageModal.saving = false;
  coverageModal.dependentIncorporationId = null;
  coverageModal.selectedEmployeeCoverageIds = [];
};

const openCoverageModal = async (record) => {
  const ticketId = record?.ticketId || ticket.value?.id;
  if (!ticketId || !record?.id) {
    notification(
      'error',
      'Error',
      'No se encontró información de ticket/carga.'
    );
    return;
  }

  coverageModal.title = `Asignar coberturas: ${record?.fullname || record?.rut || 'Carga'}`;
  coverageModal.dependentIncorporationId = record.id;
  coverageModal.selectedEmployeeCoverageIds = [];
  coverageModal.visible = true;
  coverageModal.loading = true;

  try {
    if (!availableCoverages.value.length) {
      await store.dispatch(FETCH_TICKET_AVAILABLE_COVERAGES, ticketId);
    }
    const response = await store.dispatch(
      FETCH_TICKET_DEPENDENT_INCORPORATION_COVERAGES,
      {
        ticketId,
        dependentIncorporationId: record.id
      }
    );
    const persistedCoverageIds =
      response?.employeeCoverageIncorporationIds ??
      response?.employeeCoverageIds;
    coverageModal.selectedEmployeeCoverageIds = Array.isArray(
      persistedCoverageIds
    )
      ? persistedCoverageIds
      : [];

    // Pre-select required coverages that aren't already selected
    const requiredCoverageIds = availableCoverages.value
      .filter((c) => c?.coverage?.required)
      .map((c) => c.employeeCoverageId || c.operationalId || c.id)
      .filter(Boolean);

    for (const id of requiredCoverageIds) {
      if (!coverageModal.selectedEmployeeCoverageIds.includes(id)) {
        coverageModal.selectedEmployeeCoverageIds.push(id);
      }
    }
  } catch (error) {
    closeCoverageModal();
    notification(
      'error',
      'Error',
      getApiMessage(error, 'No se pudieron cargar las coberturas de la carga.')
    );
  } finally {
    coverageModal.loading = false;
  }
};

const saveDependentCoverages = async () => {
  const ticketId = ticket.value?.id;
  const dependentIncorporationId = coverageModal.dependentIncorporationId;

  if (!isTicketEditable.value) {
    notification(
      'error',
      'Error',
      'El ticket no está editable. Solo estados Creado, En revisión corredora, o En revisión aseguradora permiten esta acción.'
    );
    return;
  }
  if (!coverageModal.selectedEmployeeCoverageIds.length) {
    notification('error', 'Error', 'Debes seleccionar al menos una cobertura.');
    return;
  }
  if (!ticketId || !dependentIncorporationId) {
    notification(
      'error',
      'Error',
      'No se encontró información de ticket/carga.'
    );
    return;
  }

  coverageModal.saving = true;
  try {
    await store.dispatch(PUT_TICKET_DEPENDENT_INCORPORATION_COVERAGES, {
      ticketId,
      dependentIncorporationId,
      payload: {
        coverageIds: coverageModal.selectedEmployeeCoverageIds
      }
    });
    notification(
      'success',
      'Correcto',
      'Coberturas actualizadas correctamente.'
    );
    closeCoverageModal();
    await store.dispatch(FETCH_TICKET_DEPENDANTS_INCORPORATIONS, ticketId);
  } catch (error) {
    notification(
      'error',
      'Error',
      getApiMessage(error, 'No se pudieron actualizar las coberturas.')
    );
  } finally {
    coverageModal.saving = false;
  }
};
</script>
